<script setup lang="ts">
/**
 * Dialog content: collapsible JSON tree + copy-to-clipboard.
 *
 * Opened via:
 *   useAppDialog().open(JsonViewerDialog, {
 *     header: '…',
 *     data: { json: payload, label: 'ABC-123' },  // label is used in the
 *                                                  //  "copied" toast
 *   });
 *
 * For back-compat, if `data` is not a `{ json, label }` envelope it's treated
 * as the JSON payload directly.
 *
 * Closes via the X in PrimeVue's dialog header — no in-body close button.
 */
import { computed, inject, ref, type Ref } from 'vue';
import JsonTree from './JsonTree.vue';
import { useNotifier } from '@/composables/notifier';

interface DialogRefValue {
  close: (data?: unknown) => void;
  data?: unknown;
}
interface JsonPayload {
  json: unknown;
  label?: string;
}

const dialogRef = inject<Ref<DialogRefValue | undefined>>('dialogRef');
const notify = useNotifier();

function isPayload(v: unknown): v is JsonPayload {
  return typeof v === 'object' && v !== null && 'json' in v;
}

const payload = computed<JsonPayload>(() => {
  const raw = dialogRef?.value?.data;
  return isPayload(raw) ? raw : { json: raw };
});

const json = computed(() => payload.value.json);
const label = computed(() => payload.value.label);

const expandAll = ref(false);
function setExpand(value: boolean): void {
  expandAll.value = value;
}

async function copyJson(): Promise<void> {
  try {
    const text = JSON.stringify(json.value, null, 2);
    await navigator.clipboard.writeText(text);
    notify.success(
      label.value ? `${label.value} copied to clipboard` : 'Copied to clipboard',
    );
  } catch {
    notify.error('Could not copy to clipboard');
  }
}
</script>

<template>
  <div class="json-viewer">
    <div class="json-viewer__toolbar">
      <button type="button" @click="setExpand(true)">Expand all</button>
      <button type="button" @click="setExpand(false)">Collapse all</button>
      <button type="button" @click="copyJson">Copy</button>
    </div>

    <div class="json-viewer__body">
      <!-- `:key` forces the tree to re-mount when the expand-all toggle flips,
           which gives JsonTree a fresh `depth` baseline for `defaultOpen`. -->
      <JsonTree
        :key="String(expandAll)"
        :value="json"
        :default-open="expandAll ? 99 : 2"
      />
    </div>
  </div>
</template>

<style scoped>
.json-viewer {
  display: flex;
  flex-direction: column;
  max-height: 75vh;
}
.json-viewer__toolbar {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem 0 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0.75rem;
}
.json-viewer__toolbar button {
  padding: 0.25rem 0.625rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  color: #374151;
  cursor: pointer;
}
.json-viewer__toolbar button:hover {
  background: #e5e7eb;
}
.json-viewer__body {
  overflow: auto;
  padding-right: 0.5rem;
}
</style>
